/**
 * useRuns - Orchestration hook for delivery runs management
 * 
 * This hook composes smaller, focused hooks to provide a unified API
 * for all run-related operations. Components import this single hook
 * and get access to all run functionality.
 * 
 * Architecture:
 * - useRunsStore: Core state + fetching (foundation)
 * - useRunSelection: Run selection logic
 * - useRunCRUD: Create/delete operations
 * - useRunCommitment: Commit/uncommit operations
 * - useStopManagement: Stop add/remove/reorder
 * - useDriverAssignment: Assign/unassign drivers
 * 
 * Options:
 * - driverMode: When true, fetches only user's assigned runs and disables mutations
 * 
 * Benefits of this composition:
 * - Each hook has a single responsibility
 * - Easier to test individual pieces
 * - Easier to understand and maintain
 * - New features follow the same pattern
 */
import { useCallback } from 'react';
import useRunsStore from './useRunsStore';
import useRunSelection from './useRunSelection';
import useRunCRUD from './useRunCRUD';
import useRunCommitment from './useRunCommitment';
import useStopManagement from './useStopManagement';
import useDriverAssignment from './useDriverAssignment';
import useRunCommands from './useRunCommands';

const useRuns = (selectedDateString, { onOrdersChange, driverMode = false, offline = false, runCacheLastUpdated = null } = {}) => {
  // Foundation: Core state and fetching
  const store = useRunsStore(selectedDateString, { driverMode, offline });
  const { isScopeCurrent } = store;
  const refreshOrders = useCallback(async options => {
    if (!isScopeCurrent()) return { state: 'retired' };
    return onOrdersChange ? onOrdersChange(options) : { state: 'refreshed' };
  }, [isScopeCurrent, onOrdersChange]);
  const commands = useRunCommands({ store, date: selectedDateString, driverMode, offline, refreshOrders });
  
  // Selection: Run selection logic
  const { selectRun } = useRunSelection({
    setSelectedRunId: store.setSelectedRunId,
    fetchRunDetails: store.fetchRunDetails
  });
  
  // CRUD: Create and delete runs
  const { handleCreateRun, handleRenameRun, handleDeleteRun, handleDiscardCachedRunProposal } = useRunCRUD({
    commands,
    selectedDateString,
    setRuns: store.setRuns,
    setSelectedRunId: store.setSelectedRunId,
    captureSelection: store.captureSelection,
    isSelectionCurrent: store.isSelectionCurrent,
    getRun: store.getRun,
    onOrdersChange: refreshOrders,
    offline,
    runCacheLastUpdated,
  });
  
  // Commitment: Commit and uncommit runs
  const { handleCommitRun, handleUncommitRun, handleCloseRun, handleStartRun, handleUnstartRun } = useRunCommitment({
    commands, offline,
    selectedRunId: store.selectedRunId,
    fetchRuns: store.fetchRuns,
    fetchRunDetails: store.fetchRunDetails,
    onOrdersChange: refreshOrders
  });
  
  // Stop Management: Add, remove, reorder stops
  const {
    handleAddToRun,
    handleRemoveFromRun,
    handleDragEnd,
    pendingReservationRepair,
    confirmReservationRepair,
    cancelReservationRepair,
  } = useStopManagement({
    commands,
    selectedRunId: store.selectedRunId,
    selectedRun: store.selectedRun,
    setRuns: store.setRuns,
    fetchRunDetails: store.fetchRunDetails,
    onOrdersChange: refreshOrders,
    captureSelection: store.captureSelection,
    isSelectionCurrent: store.isSelectionCurrent,
    isScopeCurrent,
    getRun: store.getRun,
    offline,
    runCacheLastUpdated,
  });

  // Driver Assignment: Assign/unassign drivers (users with driver role) to runs
  const { 
    handleAssignUser,
    handleUnassignUser,
    assignUserToRun,
    unassignUserFromRun,
    handleNotifyDriver
  } = useDriverAssignment({
    commands,
    selectedRunId: store.selectedRunId,
    selectedRun: store.selectedRun,
    fetchRunDetails: store.fetchRunDetails,
    fetchRuns: store.fetchRuns,
    offline,
    runCacheLastUpdated,
  });

  // Return unified API
  return {
    runCommands: commands.public,
    // State (from store)
    runs: store.runs,
    selectedRun: store.selectedRun,
    selectedRunId: store.selectedRunId,
    loadingRuns: store.loadingRuns,
    isScopeCurrent,
    captureSelection: store.captureSelection,
    isSelectionCurrent: store.isSelectionCurrent,
    getRun: store.getRun,
    
    // Fetching (from store)
    fetchRuns: store.fetchRuns,
    fetchRunDetails: store.fetchRunDetails,
    
    // Selection
    selectRun,
    clearSelectedRun: store.clearSelectedRun,
    
    // CRUD
    handleCreateRun,
    handleRenameRun,
    handleDeleteRun,
    handleDiscardCachedRunProposal,
    
    // Commitment
    handleCommitRun,
    handleUncommitRun,
    handleCloseRun,
    handleStartRun,
    handleUnstartRun,
    
    // Stop Management
    handleAddToRun,
    handleRemoveFromRun,
    handleDragEnd,

    // Stale run-reservation recovery (admin only, offered on add conflicts)
    pendingReservationRepair,
    confirmReservationRepair,
    cancelReservationRepair,

    // Driver Assignment (users with driver role)
    handleAssignUser,
    handleUnassignUser,
    assignUserToRun,
    unassignUserFromRun,
    handleNotifyDriver
  };
};

export default useRuns;
